<template>
  <v-dialog v-model="show_dialog_box" max-width="800px" persistent>
    <v-card class="pa-3" color="pop_bg">
      <v-card-title>
        <span class="text-h6 black--text">Import Subscriptions from Excel</span>
        <v-spacer></v-spacer>
        <v-btn color="black" text @click="closeDialog" :disabled="importing">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text>
        <v-container class="mt-2">
          <!-- Sample Download Section -->
          <v-row class="mb-4">
            <v-col cols="12">
              <v-alert type="info" dense outlined class="mb-3">
                <div class="d-flex align-center">
                  <span>Download the sample file to see the required format for importing subscriptions</span>
                  <v-spacer></v-spacer>
                  <v-btn color="info" outlined small @click="downloadSample" :loading="downloadingSample">
                    <v-icon left small>mdi-download</v-icon>
                    Download Sample
                  </v-btn>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- File Upload Section -->
          <v-row v-if="!fileUploaded">
            <v-col cols="12">
              <v-file-input
                v-model="selectedFile"
                accept=".xlsx,.xls"
                label="Select Excel File"
                prepend-icon="mdi-file-excel"
                show-size
                @change="onFileSelected"
                :rules="fileRules"
                outlined
                dense
              ></v-file-input>
            </v-col>
            <v-col cols="12">
              <v-btn color="primary" @click="uploadAndValidate" :disabled="!selectedFile || uploading" :loading="uploading" block>
                <v-icon left>mdi-upload</v-icon>
                Upload & Validate
              </v-btn>
            </v-col>
          </v-row>

          <!-- Validation Results Section -->
          <v-row v-if="fileUploaded && !importing">
            <v-col cols="12">
              <v-alert v-if="validationResults.validCount > 0" type="success" dense outlined> {{ validationResults.validCount }} subscriptions ready for import </v-alert>

              <v-alert v-if="validationResults.errors.length > 0" type="error" dense outlined> {{ validationResults.errors.length }} subscriptions have errors </v-alert>
            </v-col>

            <!-- Error Details -->
            <v-col cols="12" v-if="validationResults.errors.length > 0">
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="error--text">View Validation Errors</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Field</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="error in validationResults.errors" :key="error.id">
                            <td>{{ error.row }}</td>
                            <td>{{ error.field }}</td>
                            <td>{{ error.message }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <!-- Action Buttons -->
            <v-col cols="12">
              <v-row>
                <v-col cols="6">
                  <v-btn color="secondary" @click="resetUpload" outlined block>
                    <v-icon left>mdi-refresh</v-icon>
                    Upload Different File
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn color="primary" @click="importSubscriptions" :disabled="validationResults.validCount === 0" block>
                    <v-icon left>mdi-database-import</v-icon>
                    Import {{ validationResults.validCount }} Subscriptions
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- Import Progress Section -->
          <v-row v-if="importing">
            <v-col cols="12" class="text-center">
              <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
              <div class="mt-3">
                <h4>Importing subscriptions...</h4>
                <p>Please wait while we process your data.</p>
              </div>
            </v-col>
          </v-row>

          <!-- Success Section -->
          <v-row v-if="importComplete">
            <v-col cols="12" class="text-center">
              <v-icon color="success" size="64">mdi-check-circle</v-icon>
              <h4 class="mt-3">Import Completed Successfully!</h4>
              <p>{{ importResults.successCount }} subscriptions have been imported.</p>
              <v-btn color="primary" @click="closeAndRefresh" class="mt-3">
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show_dialog_box: false,
      selectedFile: null,
      uploading: false,
      importing: false,
      fileUploaded: false,
      importComplete: false,
      downloadingSample: false,
      validationResults: {
        validCount: 0,
        errors: [],
      },
      importResults: {
        successCount: 0,
      },
      fileRules: [
        v => !!v || "File is required",
        v => !v || v.size < 10000000 || "File size should be less than 10 MB",
        v => !v || ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"].includes(v.type) || "File must be Excel format (.xlsx or .xls)",
      ],
    };
  },
  watch: {
    value(newVal) {
      this.show_dialog_box = newVal;
      if (!newVal) {
        this.resetDialog();
      }
    },
    show_dialog_box(newVal) {
      this.$emit("input", newVal);
    },
  },
  methods: {
    async downloadSample() {
      this.downloadingSample = true;

      try {
        const sampleUrl = `${process.env.VUE_APP_BASE_URL}/upload-samples/subscriptions_sample.xlsx`;

        // Create a temporary link element and trigger download
        const link = document.createElement("a");
        link.href = sampleUrl;
        link.download = "subscriptions_sample.xlsx";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$toast.success("Sample file download started");
      } catch (error) {
        console.error("Download error:", error);
        this.$toast.error("Failed to download sample file");
      } finally {
        this.downloadingSample = false;
      }
    },

    onFileSelected() {
      // Reset validation when new file is selected
      this.fileUploaded = false;
      this.validationResults = {
        validCount: 0,
        errors: [],
      };
    },

    async uploadAndValidate() {
      if (!this.selectedFile) return;

      this.uploading = true;

      try {
        const result = await this.$apollo.mutate({
          mutation: require("../../api/validateSubscriptionImport.graphql"),
          variables: {
            file: this.selectedFile,
          },
        });

        if (result.data && result.data.validateSubscriptionImport) {
          this.validationResults = result.data.validateSubscriptionImport;
          this.fileUploaded = true;
        }
      } catch (error) {
        console.error("Validation error:", error);
        this.$emit("error", "Failed to validate file. Please check the format and try again.");
      } finally {
        this.uploading = false;
      }
    },

    async importSubscriptions() {
      if (this.validationResults.validCount === 0) return;

      this.importing = true;

      try {
        const result = await this.$apollo.mutate({
          mutation: require("../../api/importSubscriptions.graphql"),
          variables: {
            file: this.selectedFile,
          },
        });

        if (result.data && result.data.importSubscriptions) {
          this.importResults = result.data.importSubscriptions;
          this.importing = false;
          this.importComplete = true;
        }
      } catch (error) {
        console.error("Import error:", error);
        this.importing = false;
        this.$emit("error", "Failed to import subscriptions. Please try again.");
      }
    },

    resetUpload() {
      this.selectedFile = null;
      this.fileUploaded = false;
      this.validationResults = {
        validCount: 0,
        errors: [],
      };
    },

    resetDialog() {
      this.selectedFile = null;
      this.uploading = false;
      this.importing = false;
      this.fileUploaded = false;
      this.importComplete = false;
      this.downloadingSample = false;
      this.validationResults = {
        validCount: 0,
        errors: [],
      };
      this.importResults = {
        successCount: 0,
      };
    },

    closeDialog() {
      this.show_dialog_box = false;
    },

    closeAndRefresh() {
      this.$emit("import-complete");
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.v-expansion-panel-content >>> .v-expansion-panel-content__wrap {
  padding: 8px 16px 16px;
}
</style>
